/** @file Validates a binding table: malformed chords, reserved browser chords, conflicts within a context. */
import type { Binding, BindingError, Platform } from './types';
import { parseKeys } from './parseKeys';
import { RESERVED_CHORDS } from './reservedChords';

const RESERVED = new Set<string>([...RESERVED_CHORDS.chromium, ...RESERVED_CHORDS.firefox]);

/** Canonical chord for comparisons: resolves Mod per platform and sorts modifiers. */
function canonical(keys: string, platform: Platform): string {
  const p = parseKeys(keys, platform);
  const mods = [p.ctrl ? 'Ctrl' : '', p.meta ? 'Meta' : '', p.shift ? 'Shift' : '', p.alt ? 'Alt' : ''].filter(Boolean);
  return [...mods, p.code].join('+');
}

/** Returns every problem; an empty array means the table is usable. */
export function validateTable(bindings: Binding[], platform: Platform = 'win'): BindingError[] {
  const errors: BindingError[] = [];
  const seen = new Map<string, Binding>();
  for (const binding of bindings) {
    let key: string;
    try {
      key = canonical(binding.keys, platform);
    } catch (e) {
      errors.push({ code: 'INVALID_CHORD', binding, message: (e as Error).message });
      continue;
    }
    if (RESERVED.has(key)) errors.push({ code: 'RESERVED_KEY', binding, message: `${binding.keys} is never delivered by the browser` });
    const slot = `${binding.context}|${key}|${binding.when ?? ''}`;
    const other = seen.get(slot);
    if (other) errors.push({ code: 'CONFLICT', binding, message: `${binding.keys} in ${binding.context} is already bound to ${other.id}` });
    else seen.set(slot, binding);
  }
  return errors;
}
